<template>
  <div class="login">
    <img src="/assets/img/NodeZoneLogo.svg" />

    <form @submit.prevent="handleLogin">
      <div class="form-field">
        <input v-model="email" type="email" placeholder=" " required />
        <label>Email</label>
      </div>

      <div class="form-field">
        <input v-model="password" type="password" placeholder=" " required />
        <label>Password</label>
      </div>

      <button class="btn" type="submit" :disabled="loading">
        {{ loading ? "Logging in…" : "Login" }}
      </button>
    </form>

    <p v-if="error" class="error">{{ error }}</p>
  </div>

  <Footer />
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useAuth } from "@/composables/useAuth.js";
import { normalizePermissions } from "@/composables/usePermissions.js";
import Footer from "@/components/Footer.vue";

const email = ref("");
const password = ref("");
const error = ref("");
const loading = ref(false);

const router = useRouter();
const route = useRoute();

const auth = useAuth();
const { login } = auth;

/* ----------------------------------------------------------
   Auto-redirect if already logged in
---------------------------------------------------------- */
onMounted(() => {
  if (auth.user.value && auth.accessToken.value) {
    router.push(route.query.redirect || "/dashboard");
  }
});

/* ----------------------------------------------------------
   LOGIN HANDLER
---------------------------------------------------------- */
const handleLogin = async () => {
  if (loading.value) return;

  error.value = "";
  loading.value = true;

  try {
    //
    // 1) Perform login (sets token, user, idle timers, session cookies)
    //
    await login(email.value, password.value);

    //
    // 2) After login, user info is already loaded by fetchUser()
    //    We simply store what we need locally.
    //
    const u = auth.user.value;
    if (u) {
      localStorage.setItem("api_key", u.api_key ?? "");
      localStorage.setItem("customer_id", u.customer_id ?? "");
      localStorage.setItem("role_name", u.role_name ?? "");
      localStorage.setItem("lang", u.lang ?? "en");

      const normalized = normalizePermissions(u.permissions || []);
      localStorage.setItem("permissions", JSON.stringify(normalized));

    }
    //
    // 3) Redirect to dashboard or intended page
    //
    const redirectTo = route.query.redirect || "/dashboard";
    router.push(redirectTo);

  } catch (err) {
    console.error("LOGIN ERROR:", err);
    error.value = err.message || "Login failed";

  } finally {
    loading.value = false;
    
  }
};
// Ensure login view is always active
document.body.classList.remove("nz-disabled");
</script>

<style lang="less" scoped>
@import (reference) "@/styles/variables.less";
@import (reference) "@/styles/mixins.less";

.login {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  max-width: 500px;
  margin: 0 auto 1.8rem auto;
  padding: 2rem;
  background: #fff;
  border-radius: 12px;
  text-align: center;
  .shadowed(0.6);

  img {
    width: 380px;
    margin-bottom: 20px;
  }

  form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin: 0 auto;
    max-width: 400px;
    align-items: center;

    button {
      margin: 0 auto;
      padding: 1rem 1.5rem;
      font-size: 1.2rem;
      width: 80%;
    }
  }

  .form-field {
    position: relative;
    width: 80%;
    margin: 0 auto;
    text-align: left;
    .floating-label(@theme, @text);
  }

  .error {
    color: @danger;
    text-align: center;
    margin-top: 1rem;
    white-space: pre-line;
  }

  .responsive({
    .login {
      margin: 2rem 1rem;
      padding: 1rem;
    }
  });
}
</style>
